import { gql as apolloGql, useMutation, useQuery } from '@apollo/client';
import { gql } from '__generated__/gql';
import {
  InvestmentEntityDocumentStatusEnumType,
  InvestmentEntityDocumentType,
  InvestmentEntityDocumentTypeEnumType,
} from '__generated__/graphql';
import classNames from 'classnames';
import dayjs from 'dayjs';
import { FC, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import DataTable from 'components/DataTable';
import ErrorMessage from 'components/ErrorMessage';

import Alert from 'primitives/Alert';
import LoadingIndicator from 'primitives/LoadingIndicator';

import DocumentVaultAccounts from './DocumentVaultAccounts';
import InvestmentEntityDocumentSlideOver from './InvestmentEntityDocumentSlideOver';
import ContributionAgreementDialog from './InvestmentEntityDocumentSlideOver/ContributionAgreementDialog';
import DematReminderDialog from './InvestmentEntityDocumentSlideOver/DematReminderDialog';

const INVESTMENT_ENTITY_QUERY = gql(`
  query InvestmentEntityDocuments($id: ID!) {
    investmentEntity(id: $id) {
      id
      investmentEntityDocuments {
        id
        type
        name
        url
        status
        signingStatus
        signingLinkUrl
        createdAt
        documentDate
      }
      distributions {
        id
        distributedAt
        distributionDocument  # File URL
        closing {
          deal {
            schemeName
          }
        }
      }
    }
  }
`);

// Kept apart from the documents query on purpose: these only drive reminders, so if either
// resolver fails the documents list must still load.
const REMINDER_STATE_QUERY = gql(`
  query InvestmentEntityDocumentReminders($id: ID!) {
    investmentEntity(id: $id) {
      id
      requiresContributionAgreementSignature
      dematAccount {
        id
      }
    }
  }
`);

const EXPORT_INVESTMENT_ENTITY_DOCUMENTS_MUTATION = apolloGql`
  mutation ExportInvestmentEntityDocuments($investmentEntityId: ID!, $documentIds: [ID!]!) {
    exportInvestmentEntityDocuments(
      investmentEntityId: $investmentEntityId
      documentIds: $documentIds
    ) {
      url
    }
  }
`;

const CLEAR_KYC_VERIFY_FLAG_MUTATION = gql(`
  mutation ClearDocumentVerifyKycFlag($id: ID!) {
    updateKycVerifyFlag(id: $id, verifyKyc: false) {
      id
      verifyKyc
    }
  }
`);

const RECHECK_CONTRIBUTION_AGREEMENT_MUTATION = gql(`
  mutation RecheckContributionAgreement($investmentEntityId: ID!) {
    recheckContributionAgreement(investmentEntityId: $investmentEntityId) {
      id
      signingStatus
    }
  }
`);

const getDocumentYear = (document: { documentDate?: string | null; createdAt?: string | null }) => {
  const year = dayjs(document.documentDate || document.createdAt).year();
  return Number.isNaN(year) ? 'Others' : String(year);
};

type InvestmentEntityDocumentRow = Partial<InvestmentEntityDocumentType> & {
  isDistribution?: boolean;
};

const InvestmentEntityDocuments: FC = () => {
  const [documentOnDisplay, setDocumentOnDisplay] = useState<InvestmentEntityDocumentRow | null>(
    null
  );
  const [showContributionAgreementDialog, setShowContributionAgreementDialog] = useState(false);
  const [showDematReminderDialog, setShowDematReminderDialog] = useState(false);
  const [activeTab, setActiveTab] = useState<'documents' | 'vault'>('documents');
  const [downloadZipLoading, setDownloadZipLoading] = useState(false);
  const [downloadZipError, setDownloadZipError] = useState<Error | null>(null);
  const { investmentEntityId } = useParams<{ investmentEntityId: string }>() as {
    investmentEntityId: string;
  };

  const { loading, error, data, refetch } = useQuery(INVESTMENT_ENTITY_QUERY, {
    variables: {
      id: investmentEntityId,
    },
    // Re-checked on every visit so the reminder disappears once the investor has signed.
    fetchPolicy: 'cache-and-network',
  });
  const {
    data: reminderData,
    error: reminderError,
    refetch: refetchReminders,
  } = useQuery(REMINDER_STATE_QUERY, {
    variables: { id: investmentEntityId },
    // 'all' keeps whatever resolved even if one field errored; a failure just means no reminder.
    errorPolicy: 'all',
    fetchPolicy: 'cache-and-network',
  });
  const requiresContributionAgreementSignature =
    reminderData?.investmentEntity?.requiresContributionAgreementSignature === true;
  // Only claim "no demat details" when the lookup actually succeeded.
  const isDematMissing =
    !reminderError &&
    !!reminderData?.investmentEntity &&
    !reminderData.investmentEntity.dematAccount;
  const [clearVerifyKycFlag] = useMutation(CLEAR_KYC_VERIFY_FLAG_MUTATION);
  const [exportInvestmentEntityDocuments] = useMutation(
    EXPORT_INVESTMENT_ENTITY_DOCUMENTS_MUTATION
  );

  const [recheckContributionAgreement] = useMutation(RECHECK_CONTRIBUTION_AGREEMENT_MUTATION);
  const hasRechecked = useRef(false);
  const hasPendingContributionAgreement = !!data?.investmentEntity.investmentEntityDocuments.some(
    doc =>
      doc.type === InvestmentEntityDocumentTypeEnumType.ContributionAgreement &&
      doc.signingStatus !== 'SIGNED'
  );

  // Once per visit: a signature whose webhook never arrived is picked up from Leegality, and the
  // reminder below disappears after the refetch.
  useEffect(() => {
    if (!hasPendingContributionAgreement || hasRechecked.current) return;
    hasRechecked.current = true;

    recheckContributionAgreement({ variables: { investmentEntityId } })
      .then(result => {
        if (result.data?.recheckContributionAgreement?.signingStatus === 'SIGNED') refetch();
      })
      .catch(() => undefined);
  }, [hasPendingContributionAgreement, investmentEntityId, recheckContributionAgreement, refetch]);

  if (loading && !data) return <LoadingIndicator />;

  if (error || !data) return <ErrorMessage error={error} refetch={refetch} />;

  const allDocuments = data.investmentEntity.investmentEntityDocuments;
  const contributionAgreement = allDocuments.find(
    doc => doc.type === InvestmentEntityDocumentTypeEnumType.ContributionAgreement
  );
  const showContributionAgreementAlert =
    contributionAgreement && contributionAgreement.signingStatus !== 'SIGNED';

  // A DRAFT (unsigned) CA is only ever surfaced via the alert above, not as a document row -
  // it has no downloadable file yet. Once signed (ACTIVE) it appears in the table like any
  // other document.
  const documents = allDocuments.filter(
    doc =>
      doc.type !== InvestmentEntityDocumentTypeEnumType.ContributionAgreement ||
      doc.status === InvestmentEntityDocumentStatusEnumType.Active
  );
  const distributions = data?.investmentEntity.distributions || [];

  const transformedDistributions: InvestmentEntityDocumentRow[] = distributions.map(doc => {
    return {
      id: doc.id,
      name: `Distribution Statement - ${doc.closing?.deal?.schemeName}`,
      type: InvestmentEntityDocumentTypeEnumType.DistributionStatement,
      url: doc.distributionDocument || '',
      createdAt: doc.distributedAt || '',
      documentDate: doc.distributedAt || '',
      isDistribution: true,
    };
  });

  const sortedDocuments = [...documents, ...transformedDistributions].sort((a, b) => {
    const dateA = dayjs(a.documentDate || a.createdAt).valueOf() || 0;
    const dateB = dayjs(b.documentDate || b.createdAt).valueOf() || 0;
    return dateB - dateA;
  });

  const tabs = [
    {
      key: 'documents',
      label: 'Investment Entity Documents',
    },
    {
      key: 'vault',
      label: 'Vault',
    },
  ] as const;

  return (
    <>
      <InvestmentEntityDocumentSlideOver
        open={!!documentOnDisplay}
        documentId={documentOnDisplay?.id ?? ''}
        prefetchedDocument={documentOnDisplay?.isDistribution ? documentOnDisplay : null}
        onClose={() => setDocumentOnDisplay(null)}
        onDocumentOpened={({ id, verifyKyc }) => {
          // Same gate as the old accreditation popup: only documents flagged verifyKyc prompt,
          // and the flag is cleared once a popup has been shown so it appears only once.
          if (!verifyKyc) return;

          // Investors who can't sign a CA are nudged to complete their demat details instead.
          if (requiresContributionAgreementSignature) {
            setShowContributionAgreementDialog(true);
          } else if (isDematMissing) {
            setShowDematReminderDialog(true);
          } else {
            return;
          }

          clearVerifyKycFlag({ variables: { id } }).catch(() => undefined);
        }}
      />
      {showContributionAgreementDialog && (
        <ContributionAgreementDialog
          investmentEntityId={investmentEntityId}
          open={showContributionAgreementDialog}
          onClose={() => {
            setShowContributionAgreementDialog(false);
            refetch();
            refetchReminders();
          }}
        />
      )}
      {showDematReminderDialog && (
        <DematReminderDialog
          investmentEntityId={investmentEntityId}
          open={showDematReminderDialog}
          onClose={() => setShowDematReminderDialog(false)}
        />
      )}
      <div className="flex flex-col gap-4">
        {requiresContributionAgreementSignature && !showContributionAgreementAlert && (
          <Alert
            type="warning"
            title="Sign your Contribution Agreement"
            message="Your investments in AL Trust need a signed Contribution Agreement for this investment entity. It takes a couple of minutes."
            buttonText="Review & Sign"
            onClick={() => setShowContributionAgreementDialog(true)}
          />
        )}
        {showContributionAgreementAlert && (
          <Alert
            type="warning"
            title="Your Contribution Agreement needs your signature"
            message="Please review and e-sign your Contribution Agreement. It only takes a couple of minutes."
            buttonText="Sign Now"
            onClick={() => window.open(contributionAgreement!.signingLinkUrl!, '_blank')}
          />
        )}
        <nav className="flex gap-x-1 overflow-x-auto" aria-label="Tabs">
          {tabs.map(tab => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveTab(tab.key)}
              className={classNames(
                activeTab === tab.key
                  ? 'bg-indigo-100 text-indigo-700'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50',
                'rounded-md px-3 py-2 text-sm font-medium text-nowrap min-w-fit'
              )}
            >
              {tab.label}
            </button>
          ))}
        </nav>
        {activeTab === 'documents' && (
          <>
            {downloadZipError && (
              <ErrorMessage
                type="alert"
                title="Unable to export ZIP"
                message={downloadZipError.message}
                error={downloadZipError}
                showLoginButton={false}
              />
            )}
            <DataTable
              data={sortedDocuments}
              onClick={document => setDocumentOnDisplay(document as InvestmentEntityDocumentRow)}
              segregation={{
                getKey: document => getDocumentYear(document),
              }}
              bulkSelectActions={[
                {
                  label: 'Download',
                  loading: downloadZipLoading,
                  action: async selectedDocumentIds => {
                    setDownloadZipLoading(true);
                    setDownloadZipError(null);

                    try {
                      const { data } = await exportInvestmentEntityDocuments({
                        variables: {
                          investmentEntityId,
                          documentIds: selectedDocumentIds,
                        },
                      });
                      const zipFileUrl = data?.exportInvestmentEntityDocuments?.url;

                      if (!zipFileUrl) {
                        throw new Error('Unable to export documents.');
                      }

                      const link = document.createElement('a');

                      link.href = zipFileUrl;
                      link.target = '_blank';
                      link.rel = 'noreferrer';
                      document.body.appendChild(link);
                      link.click();
                      link.remove();
                    } catch (error) {
                      setDownloadZipError(
                        error instanceof Error ? error : new Error('Unable to export documents.')
                      );
                      throw error;
                    } finally {
                      setDownloadZipLoading(false);
                    }
                  },
                },
              ]}
              columns={[
                {
                  label: 'Select',
                  fieldName: 'id',
                  type: 'SELECT',
                },
                {
                  label: 'Name',
                  fieldName: 'name',
                },
                {
                  label: 'Type',
                  fieldName: 'type',
                  type: 'INVESTMENT_ENTITY_DOCUMENT_TYPE',
                },
                {
                  label: 'Added on',
                  fieldName: 'createdAt',
                  type: 'DATE',
                },
              ]}
              emptyListTitle="No documents to show"
              emptyListDescription="Documents will appear here once they are uploaded."
            />
          </>
        )}
        {activeTab === 'vault' && (
          <div className="my-4">
            <DocumentVaultAccounts investmentEntityId={investmentEntityId} />
          </div>
        )}
      </div>
    </>
  );
};

export default InvestmentEntityDocuments;
